import React, { useEffect, useRef, useMemo } from 'react';
import ApexCharts from 'apexcharts';

const AreaChart = ({ data, title = 'Revenue Trend' }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Memoize formatted series data (convert string dates → timestamps)
  const series = useMemo(() => {
    if (!data?.revenueTrend || !Array.isArray(data.revenueTrend)) {
      return [{ name: 'Income', data: [] }];
    }

    const formattedData = data.revenueTrend.map((entry) => {
      // Parse date string → Unix timestamp (ms)
      // Adjust parse logic if your date format is unusual (e.g. "DD-MM-YYYY")
      const timestamp = new Date(entry.date).getTime();
      return [timestamp, entry.revenue];
    });

    return [{ name: 'Income', data: formattedData }];
  }, [data]);

  const options = useMemo(
    () => ({
      chart: {
        type: 'area',
        height: 350,
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
          animateGradually: { enabled: true, delay: 150 },
          dynamicAnimation: { enabled: true, speed: 350 },
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.3,
          stops: [0, 90, 100],
        },
      },
      xaxis: {
        type: 'datetime',           // ← Key change!
        labels: {
          style: {
            colors: '#8e8da4',
            fontSize: '12px',
            fontFamily: 'Inter, sans-serif',
          },
          // Built-in dynamic formatting (ApexCharts chooses based on range)
          datetimeFormatter: {
            year: 'yyyy',
            month: 'MMM \'yy',     // e.g. Jan '26
            day: 'dd MMM',         // e.g. 15 Jan
            hour: 'HH:mm',         // e.g. 14:30

          },
          // Optional: hide duplicate labels when zooming
          showDuplicates: false,
        },
        axisBorder: { show: true, color: '#e0e0e0' },
        axisTicks: { show: true, color: '#e0e0e0', height: 6 },
        crosshairs: {
          show: true,
          position: 'front',
          stroke: { color: '#3B82F6', width: 1, dashArray: 3 },
        },
        tooltip: { enabled: true },
      },
      yaxis: {
        title: {
          text: 'Revenue',
          style: { color: '#8e8da4', fontSize: '14px', fontFamily: 'Inter, sans-serif' },
        },
        labels: {
          style: { colors: '#8e8da4', fontSize: '12px', fontFamily: 'Inter, sans-serif' },
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        floating: true,
        offsetY: -25,
        offsetX: -5,
      },
      colors: ['#3B82F6'],
      markers: {
        size: 4,
        colors: ['#3B82F6'],
        strokeColors: ['#fff'],
        strokeWidth: 2,
        opacity: 0.9,
      },
      tooltip: {
        theme: 'light',
        shared: true,
        intersect: false,
        x: {
          format: 'dd MMM yyyy HH:mm', // tooltip date format (can be more precise)
        },
      },
    }),
    [] // no dependencies — options are static except series
  );

  useEffect(() => {
    if (!chartRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
      chartInstance.current = null;
    }

    const finalOptions = {
      ...options,
      series,
    };

    const chart = new ApexCharts(chartRef.current, finalOptions);
    chart.render();
    chartInstance.current = chart;

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [options, series]); // Re-render when series (data) changes

  return (
    <div
      className="max-w-4xl mx-auto bg-white rounded-sm shadow-sm"
      style={{ padding: '1.4rem', minHeight: '320px' }}
    >
      <h2 className="text-xl font-semibold text-gray-800 mb-4">{title}</h2>
      <div ref={chartRef} />
    </div>
  );
};

export default AreaChart;